import { useCallback } from 'react'
import { useIntl } from 'react-intl'

import { cn, focusRing, targetSize } from '@/shared/ui/cn'
import { XCircle } from '@/shared/ui/icons'
import type { FocusRef, ListFocus } from '@/shared/ui/use-list-focus'

import type { SearchFilterField } from './definition'
import { useDescribeFilter } from './labels'
import type { FieldFilter } from './search-state'

/**
 * The mockups' applied-filter pill (`components/find-search/FilterChip.jsx`): the filter's text
 * and a remove button. It grows to fit its text and never cuts it off.
 */
export function FilterChip(props: { readonly label: string; readonly onRemove: () => void; readonly removeRef?: FocusRef }) {
  const intl = useIntl()
  const { label, onRemove, removeRef } = props

  return (
    <span className="inline-flex min-h-[29px] max-w-full items-center gap-2 rounded-full bg-primary-main py-1 pl-4 pr-2.5 font-sans text-sm font-normal leading-[1.2] text-primary-contrast transition hover:bg-primary-dark">
      <span className="min-w-0 break-words">{label}</span>
      <button
        ref={removeRef}
        type="button"
        onClick={onRemove}
        aria-label={intl.formatMessage({ id: 'search.chip.remove' }, { filter: label })}
        // focusRing's primary outline would vanish against the chip's primary fill.
        className="flex shrink-0 cursor-pointer items-center rounded-full hover:opacity-70 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-contrast"
      >
        <XCircle size={18} filled />
      </button>
    </span>
  )
}

/** The key of an applied filter's chip. */
export const chipKey = (filter: FieldFilter) => JSON.stringify(filter)

/** The key of the chip that says inactive records are shown. */
export const inactiveChipKey = 'includeInactive'

/**
 * The mockups' "Filters active:" row (`components/find-search/ActiveFilters.jsx`) below the toolbar: a
 * chip for each applied filter, then Clear All. Nothing when no filter applies.
 */
export function FilterChips(props: {
  readonly fields: readonly SearchFilterField[]
  readonly filters: readonly FieldFilter[]
  /** The "Show deactivated" (or disabled, or archived) chip, first, while it applies. */
  readonly inactive?: { readonly label: string; readonly onRemove: () => void } | undefined
  /** Keeps focus on a chip, by its {@link chipKey} or {@link inactiveChipKey}, as the one before it is removed. */
  readonly focus?: ListFocus
  readonly onRemove: (filter: FieldFilter) => void
  readonly onClearAll: () => void
}) {
  const intl = useIntl()
  const describe = useDescribeFilter(props.fields)
  const { inactive } = props
  if (props.filters.length === 0 && inactive === undefined) {
    return null
  }
  const inactiveRef = props.focus?.control(inactiveChipKey)

  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <span className="text-sm text-text-primary">{intl.formatMessage({ id: 'search.chips.filtersActive' })}</span>
      <ul className="flex max-w-full flex-wrap items-center gap-2">
        {inactive !== undefined && (
          <li className="max-w-full">
            <FilterChip label={inactive.label} onRemove={inactive.onRemove} {...(inactiveRef === undefined ? {} : { removeRef: inactiveRef })} />
          </li>
        )}
        {props.filters.map((filter) => (
          <li key={chipKey(filter)} className="max-w-full">
            <AppliedChip filter={filter} label={describe(filter)} removeRef={props.focus?.control(chipKey(filter))} onRemove={props.onRemove} />
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={props.onClearAll}
        className={cn('ml-2 cursor-pointer rounded px-1 text-sm font-medium text-primary-main hover:underline', targetSize, focusRing)}
      >
        {intl.formatMessage({ id: 'search.filters.clearAll' })}
      </button>
    </div>
  )
}

function AppliedChip(props: {
  readonly filter: FieldFilter
  readonly label: string
  readonly removeRef: FocusRef | undefined
  readonly onRemove: (filter: FieldFilter) => void
}) {
  const { filter, label, removeRef, onRemove } = props
  const remove = useCallback(() => onRemove(filter), [filter, onRemove])
  return <FilterChip label={label} onRemove={remove} {...(removeRef === undefined ? {} : { removeRef })} />
}
